import { css } from '@emotion/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { useTranslation } from '../../../hooks/useTranslation';
import colors from '../../../themes/main-colors';
import { useIsMobile } from '../../../util/style-utils';
import Slider from '@material-ui/core/Slider';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

import {
  Box,
  Card,
  CardContent,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import CurrencyTextField from '../../currency-text-field';
import Tooltip from '../../tooltip';

import CloseIcon from '@material-ui/icons/Close';
import { RECONNECT_PAY_OPTIONS } from '../../reconnect-service/reconnectServicePayment.types';
import { FormState } from '../../../hooks/useFormState.types';
import { Model } from '../payment-info-form/PaymentInfoForm.rules';
import { OneTimePayConfigData } from '../oneTimePayment.types';
import {
  formatDate,
  getFormattedOneYearDate,
  toCurrencyDisplayFormat,
  toCurrencyString,
  toDateStringFullMonthName,
} from '../../../util/format';
import { parse } from 'date-fns';
import useStyles from '../PaymentForm.styles';
import moment from 'moment';

import DatePicker from '../../date-picker';
import { getPaymentMethodLabel } from '../../../hooks/usePaymentus';
import PaymentProfileIcon from '../../paymentus/payment-profile-icon';
import {
  getAccountDisplayString,
  getPmCategoryFromType,
} from '../../paymentus/utils';
import Dropdown from '../../dropdown';

import InfoIcon from '@material-ui/icons/Info';
import {
  PaymentInfo,
  roundUpToNearestFive,
  roundUpToNearestTen,
  roundUpToNextDollar,
} from '../oneTimePay.utils';
import { isDateInFuture } from '../../auto-pay/auto-pay.utils';
import { PaymentCategory } from '../../../__generated__/pge-types';

const useSliderStyles = makeStyles<Theme>(theme =>
  createStyles({
    topConatiner: {
      backgroundColor: colors.riverBlue,
    },
    reconnectContainer: {
      padding: theme.spacing(2),
    },
    columnview: {
      padding: theme.spacing(0, 4, 2, 4),
      '& .MuiGrid-container': {
        marginBottom: theme.spacing(2),
      },
    },

    infoContainer: {
      backgroundColor: colors.white,
      padding: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    inputContainer: {
      padding: theme.spacing(0, 3, 0, 0),
      justifyContent: 'center',
      flexDirection: 'column',
      display: 'flex',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.67)',
      },
    },
    labelText: {
      width: '50%',
      fontSize: '18px',
      color: colors.noirBlur,
    },
    pastDueLable: {
      width: '50%',
      fontSize: '18px',
      fontWeight: 'bold',
      color: colors.roseRed,
    },
    labelValue: {
      width: '50%',
      fontSize: '20px',
      color: colors.noirBlur,
      fontWeight: 'bold',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    margin1: {
      marginTop: '1em !important',
      marginBottom: '1em !important',
    },
    cardRow: {
      marginTop: theme.spacing(2),
    },
    link: {
      textDecoration: 'underline',
      fontWeight: 'bold',
      cursor: 'pointer',
    },
    label: {
      fontWeight: 'bold',
    },
    mobileView: {
      display: 'grid',
      marginBottom: theme.spacing(2),
    },
    mobileCard: {
      borderBotton: `3px solid ${colors.sparkBlue}`,
      borderTop: `3px solid ${colors.sparkBlue}`,
    },
    charityMobileView: {
      background: colors.riverBlue,
      padding: theme.spacing(2),
    },
    paymentMethodIcon: {
      width: '1.5em',
      height: 'auto',
      verticalAlign: 'inherit',
      marginRight: '0.2em',
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
      },
    },
    circularDiv: {
      position: 'relative',
      width: '300px',
      height: '300px',
      textAlign: 'center',
    },
    slider: {
      marginTop: theme.spacing(2),
      '& .MuiSvgIcon-root': {
        width: '0.8em',
        height: '0.8em',
        fontSize: '50px !important',
        color: '#fff !important',
      },
      '& .MuiSlider-thumb': {
        border: '2px solid #fff',
        boxShadow: 'none',
        width: '45px',
        height: '45px',
        position: 'absolute',
        top: '5px',
        left: '50%',
        transform: 'translateX(-30%)',
        zIndex: 1,
        background: colors.noirBlur,
        [theme.breakpoints.down('sm')]: {
          top: '11px',
        },
        '&:before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 24,
          height: 24,
          backgroundColor: 'transparent',
          backgroundSize: '24px 24px',
          backgroundRepeat: 'no-repeat',
        },
      },
      '& .MuiSlider-track': {
        height: 20,
        border: '2px solid #000000',
        borderRadius: '10px',
        '&:after': {
          transform: 'translateX(-45px)',
        },
      },
      '& .MuiSlider-rail': {
        border: '2px solid #000000',
        height: '20px',
        borderRadius: '10px',
        '&:after': {
          transform: 'translateX(-45px)',
        },
      },
    },
  }),
);

interface Props {
  isForReconnect?: boolean;
  paymentInfoFormState: FormState<Model>;
  oneTimePayConfig: OneTimePayConfigData;
  //amountToBePaid?: string;
  setReconnectPayAmount: (amt: string) => void;
  setOneTimePayConfig: React.Dispatch<React.SetStateAction<any>>;
  reconnectPayAmount: string;
  minimumAmount?: string;
  fullAmount?: string;
  //account?: AccountDetail;
  //calendarEndDate?: Date;
  paymentInfo: PaymentInfo;
  disableAmountField?: boolean;
  cardFooterContent?: React.ReactNode;
  enableDonation?: boolean;
  disableDateField?: boolean;
  isInvoicePayment?: boolean;
}

const Thumb: React.FC<any> = thumbProps => {
  return (
    <span {...thumbProps}>
      <KeyboardArrowRightIcon
        style={{
          fontSize: 24,
          color: '#000',
        }}
      />
    </span>
  );
};

const useStylesForToolTip = makeStyles(() => ({
  tooltip: {
    background: '#FFFFFF 0% 0% no-repeat padding-box',
    border: '3px solid #F3AD3C',
    borderRadius: '5px',
    opacity: 1,
  },
  arrow: {
    color: '#F3AD3C',
  },
}));
const PaymentSlider: FunctionComponent<Props> = props => {
  const classes = useStyles();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [railWidth, setRailWidth] = useState(0);

  const isMobile = useIsMobile();
  const { t, richT } = useTranslation();
  const [sliderValue, setSliderValue] = useState(0.0);
  const PstTime = moment()
    .tz('Canada/Pacific')
    .format('MM/DD/yyyy');
  const classesForToolTip = useStylesForToolTip();
  const today = formatDate(new Date(PstTime), 'MM/dd/yyyy');
  const datePickerEndDate = props.oneTimePayConfig.maxPaymentDate
    ? formatDate(props.oneTimePayConfig.maxPaymentDate)
    : getFormattedOneYearDate(new Date(PstTime));

  const handleChangeCommited = (
    event: any,
    newValue: number | Array<number>,
  ) => {
    let paymentAmount = newValue;
    if ((newValue as number) < parseFloat(props.minimumAmount || '0')) {
      paymentAmount = parseFloat(props.minimumAmount || '0');
    }
    void props.paymentInfoFormState.onChange({
      target: {
        name: 'paymentAmount',
        value: Number(paymentAmount)
          .toFixed(2)
          .toString(),
      },
    } as React.ChangeEvent<HTMLInputElement>);
    props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
      ...prevConfig,
      paymentAmount: Number(paymentAmount)
        .toFixed(2)
        .toString(),
    }));
  };
  const handleSliderChange = (event: any, newValue: any) => {
    if (!newValue) {
      return;
    }

    if (newValue > parseInt(props.minimumAmount || '0')) {
      setSliderValue(newValue);
    }
  };

  const handleCharityAmountChange = async (newValue: any) => {
    if (!newValue) {
      return;
    }
    props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
      ...prevConfig,
      charitySelection: newValue,
    }));

    const paymentAmount =
      Number(props.paymentInfoFormState?.values.paymentAmount) || 0;
    await props.paymentInfoFormState.onChange({
      target: { name: 'charitySelection', value: newValue.toString() },
    } as React.ChangeEvent<HTMLInputElement>);
    let roundupAmount = 0;
    if (newValue !== 'none' && props?.enableDonation) {
      switch (newValue) {
        case '0':
          roundupAmount = roundUpToNextDollar(paymentAmount);
          break;
        case '5':
          roundupAmount = roundUpToNearestFive(paymentAmount);
          break;
        case '10':
          roundupAmount = roundUpToNearestTen(paymentAmount);
          break;
        default:
          paymentAmount;
          break;
      }

      void props.paymentInfoFormState.onChange({
        target: {
          name: 'charityAmount',
          value: (roundupAmount - paymentAmount).toFixed(2).toString(),
        },
      } as React.ChangeEvent<HTMLInputElement>);

      props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
        ...prevConfig,
        charityAmount: roundupAmount - paymentAmount,
      }));

      void props.paymentInfoFormState.onChange({
        target: {
          name: 'paymentDate',
          value: today,
        },
      } as React.ChangeEvent<HTMLInputElement>);

      props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
        ...prevConfig,
        paymentDate: today,
      }));

      props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
        ...prevConfig,
        splitPayStatus: false,
      }));
    } else {
      void props.paymentInfoFormState.onChange({
        target: {
          name: 'charityAmount',
          value: '0.00',
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const [selectedOption, setSelectedOption] = useState<RECONNECT_PAY_OPTIONS>(
    props.oneTimePayConfig?.selectedReconnectOption!,
  );
  const [isOpen, toggleOpen] = useState(false);

  const onCloseTooltip = () => {
    toggleOpen(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setSliderValue(Number(props.oneTimePayConfig.paymentAmount || 0));
    if (props.isForReconnect) {
      handleOptionChange(props.oneTimePayConfig?.selectedReconnectOption);
    }
  }, []);

  useEffect(() => {
    if (!props?.enableDonation) {
      props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
        ...prevConfig,
        charitySelection: 'none',
        charityAmount: '0.00',
      }));
    }
  }, [props?.enableDonation]);

  useEffect(() => {
    if (props?.oneTimePayConfig?.splitPayStatus) {
      props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
        ...prevConfig,
        charitySelection: undefined,
        charityAmount: '0.00',
      }));
      void props.paymentInfoFormState.onChange({
        target: { name: 'charitySelection', value: '' },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [props?.oneTimePayConfig?.splitPayStatus]);

  const onPaidAmountChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const amountDue = props?.paymentInfo?.amountDue || 0.0;
    const payAmount = Number(event.target.value);
    props.isForReconnect
      ? props?.setReconnectPayAmount(event.target.value)
      : null;
    const action =
      (amountDue <= 0 && payAmount >= 100) ||
      (amountDue > 0 && payAmount >= amountDue * 2);
    await props?.paymentInfoFormState.onChange(event);
    setSliderValue(payAmount);
    toggleOpen(action);
    await handleCharityAmountChange(props?.oneTimePayConfig?.charitySelection);
  };

  const onDateChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await props?.paymentInfoFormState.onChange(event);
    await handleCharityAmountChange('none');
  };
  const handleOptionChange = (
    optionSelection: RECONNECT_PAY_OPTIONS = 'RECONNECT_PAY_MINIMUM_DUE',
  ) => {
    props.paymentInfoFormState.reset();
    if (props.isForReconnect) {
      setSelectedOption(optionSelection);
      if (optionSelection === 'RECONNECT_PAY_MINIMUM_DUE') {
        props.setReconnectPayAmount(props.minimumAmount!);
      }
      if (optionSelection === 'RECONNECT_PAY_FULL_AMOUNT') {
        props.setReconnectPayAmount(props.fullAmount!);
      }
      if (optionSelection === 'RECONNECT_PAY_OTHER_AMOUNT') {
        props.setReconnectPayAmount(props.reconnectPayAmount);
      }
      props.setOneTimePayConfig((config: OneTimePayConfigData) => {
        config.selectedReconnectOption = optionSelection;
        config.fullAmount = props.fullAmount;
        config.minimumAmount = props.minimumAmount;
        return { ...config };
      });
    }
  };

    const pastDueAmount = props?.paymentInfo?.pastDueAmount || 0;
  const sliderClasses = useSliderStyles();
  const CharityTooltip = () => {
    return (
      <Box>
        <Tooltip
          title={t('CHARITY_TOOLTIP')}
          classes={classesForToolTip}
          enterTouchDelay={0}
        >
          <InfoIcon />
        </Tooltip>
      </Box>
    );
  };

  const CharitySection = () => {
    return (
      <React.Fragment>
        <Grid
          item
          xs={isMobile ? 12 : 7}
          container
          direction="row"
          justify="space-between"
          style={isMobile ? { marginBottom: '16px' } : {}}
          alignItems="center"
        >
          <Box style={isMobile ? { width: '90%' } : { width: '100%' }}>
            <Typography variant={'body2'} className={sliderClasses.label}>
              {t('CHARITY_TEXT')}
            </Typography>
          </Box>

          {isMobile && <CharityTooltip />}
        </Grid>

        <Grid
          item
          xs={isMobile ? 12 : 5}
          container
          direction="row"
          justify="space-between"
          style={{ marginBottom: 0 }}
          alignItems="center"
        >
          <Box style={!isMobile ? { width: '90%' } : { width: '100%' }}>
            <Dropdown
              label={t('SELECT_CHARITY_LABEL')}
              disabled={isDateInFuture(
                props?.paymentInfoFormState
                  ?.props('paymentDate')
                  ?.value?.toString(),
              )}
              defaultValue={props?.oneTimePayConfig?.charitySelection || ''}
              {...props?.paymentInfoFormState.props('charitySelection')}
              selectionList={[
                { value: '0', label: t('CHARITY_OPTION_1') },
                { value: '5', label: t('CHARITY_OPTION_2') },
                { value: '10', label: t('CHARITY_OPTION_3') },
                { value: 'none', label: t('CHARITY_OPTION_4') },
              ]}
              onChange={e => handleCharityAmountChange(e.target.value)}
            />
          </Box>
          {!isMobile && <CharityTooltip />}
        </Grid>
        {isDateInFuture(
          props?.paymentInfoFormState?.props('paymentDate')?.value?.toString(),
        ) && (
          <Grid item>
            <Box
              style={{
                marginTop: '10px',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <InfoIcon />
              <Typography variant="body2" style={{ marginLeft: '2px' }}>
                {t('CHARITY_DATE_MESSAGE')}
              </Typography>
            </Box>
          </Grid>
        )}

        {props?.oneTimePayConfig?.charitySelection &&
          props?.oneTimePayConfig?.charitySelection !== 'none' && (
            <Grid item style={{ marginTop: '10px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name={'charityConfirm'}
                    checked={props.paymentInfoFormState.values.charityConfirm}
                    color={'primary'}
                    onChange={props.paymentInfoFormState.onChange}
                  />
                }
                label={t('CHARITY_CONFIRMATION')}
              />
              {props.paymentInfoFormState.errors.charityConfirm && (
                <FormHelperText error={true}>
                  {props.paymentInfoFormState.errors.charityConfirm}
                </FormHelperText>
              )}
            </Grid>
          )}
      </React.Fragment>
    );
  };

  const PaymentMethodSection: React.FC<any> = () => {
    return (
      <React.Fragment>
        {props.oneTimePayConfig?.paymentSelector?.selectedPaymentProfileList &&
          props.oneTimePayConfig?.paymentSelector?.selectedPaymentProfileList.map(
            (paymentProfile, i) => {
              const pmCategory = getPmCategoryFromType(
                paymentProfile?.profile.type,
              );
              return (
                <Grid
                  key={i}
                  item
                  container
                  direction="row"
                  className={sliderClasses.cardRow}
                >
                  <Typography
                    className={sliderClasses.paymentMethodIcon}
                    component="span"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    variant={'body2'}
                  >
                    <PaymentProfileIcon
                      type={paymentProfile.profile?.type}
                    ></PaymentProfileIcon>
                    <Typography
                      variant={'body1'}
                      component="span"
                      style={{
                        marginLeft: '0.5em',
                        fontSize: '1rem',
                      }}
                    >
                      {pmCategory === PaymentCategory.AmazonPay ||
                      pmCategory === PaymentCategory.PaypalAccount ||
                      pmCategory === PaymentCategory.PaypalCredit ||
                      pmCategory === PaymentCategory.Ap ||
                      pmCategory === PaymentCategory.Venmo
                        ? t('ONETIMEPAY_USIGN')
                        : t('ONETIMEPAY_USIGN') +
                          (' ' +
                            getPaymentMethodLabel(
                              paymentProfile?.profile.type,
                              true,
                            ))}
                    </Typography>{' '}
                    <Typography
                      variant={'body1'}
                      style={{
                        verticalAlign: 'middle',
                        display: 'inline',
                        fontSize: '1rem',
                        marginLeft: '0.5em',
                      }}
                    >
                      {getAccountDisplayString(paymentProfile?.profile) ||
                        t('GUEST_PAY_AUTHORIZED')}
                    </Typography>
                  </Typography>
                </Grid>
              );
            },
          )}
        {!isMobile && <Divider />}
        <Grid item className={sliderClasses.margin1}>
          <Link
            onClick={() => {
              props.setOneTimePayConfig((prevConfig: OneTimePayConfigData) => ({
                ...prevConfig,
                selectedAccordian: 0,
              }));
              document.getElementById('walletSection')?.scrollIntoView();
            }}
            className={sliderClasses.link}
          >
            {props?.oneTimePayConfig?.paymentSelector.savedProfileList
              ?.length! > 0
              ? t('PAYMENT_METHOD_LINK')
              : t('ADD_A_PAYMENT_METHOD')}
          </Link>
        </Grid>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      {!isMobile && (
        <Card>
          <CardContent style={{ padding: '0' }}>
            <Grid
              container
              alignItems="stretch"
              className={sliderClasses.topConatiner}
            >
              {props?.isForReconnect ? (
                <Grid
                  item
                  container
                  direction="row"
                  className={sliderClasses.reconnectContainer}
                >
                  <Grid
                    item
                    container
                    direction={'column'}
                    xs={12}
                    style={{ marginBottom: '1em' }}
                  >
                    <Typography
                      className={classes.colorTextPrimary}
                      component={'span'}
                      variant={'h6'}
                      style={{ fontWeight: 'bold' }}
                    >
                      {t('SELECT_PAYMENT_AMOUNT')}
                    </Typography>
                  </Grid>
                  <Grid item container direction={'column'} xs={12}>
                    <Typography variant="body2">
                      {t('RECONNECT_SUB_TITLE_TEXT')}
                    </Typography>
                  </Grid>
                  <Grid item container direction={'column'} xs={12}>
                    <RadioGroup
                      value={selectedOption || ''}
                      onChange={e => {
                        handleOptionChange(
                          e.target.value as RECONNECT_PAY_OPTIONS,
                        );
                      }}
                    >
                      <FormControlLabel
                        value={'RECONNECT_PAY_MINIMUM_DUE'}
                        className={classes.formRadioCls}
                        control={
                          <Radio
                            color={'primary'}
                            disabled={props.oneTimePayConfig.splitPayStatus}
                          />
                        }
                        label={
                          <Grid
                            container
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Grid item>
                              <Typography variant="body2">
                                {t('MIN_OPTION')}
                              </Typography>
                            </Grid>
                            <Grid item style={{ paddingLeft: '0px' }}>
                              <Typography
                                variant="body2"
                                className={classes.radioOptionAmountText}
                              >
                                $
                                {toCurrencyDisplayFormat(
                                  Number(props.minimumAmount) || 0,
                                  true,
                                  'CR',
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                      />
                      <FormControlLabel
                        value={'RECONNECT_PAY_FULL_AMOUNT'}
                        className={classes.formRadioCls}
                        control={
                          <Radio
                            color={'primary'}
                            disabled={props.oneTimePayConfig.splitPayStatus}
                          />
                        }
                        label={
                          <Grid
                            container
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Grid item>
                              <Typography variant="body2">
                                {t('FULL_OPTION')}
                              </Typography>
                            </Grid>
                            <Grid item style={{ paddingLeft: '0px' }}>
                              <Typography
                                variant="body2"
                                className={classes.radioOptionAmountText}
                              >
                                $
                                {toCurrencyDisplayFormat(
                                  Number(props.fullAmount) || 0,
                                  true,
                                  'CR',
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                      />
                      <Grid
                        container
                        direction={isMobile ? 'column' : 'row'}
                        spacing={1}
                      >
                        <Grid item xs={isMobile ? 12 : 3}>
                          <FormControlLabel
                            className={classes.formRadioCls}
                            style={{ fontSize: '1em', marginRight: '0px' }}
                            value={'RECONNECT_PAY_OTHER_AMOUNT'}
                            control={
                              <Radio
                                color={'primary'}
                                disabled={props.oneTimePayConfig.splitPayStatus}
                              />
                            }
                            label={
                              <Grid
                                container
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Grid item>
                                  <Typography variant="body2">
                                    {t('OTHER_OPTION_TEXT_PART_1')}
                                  </Typography>
                                </Grid>
                                <Grid item style={{ paddingLeft: '0px' }}>
                                  <Typography
                                    variant="body2"
                                    className={classes.radioOptionAmountText}
                                  >
                                    {t('OTHER_OPTION_TEXT_PART_2')}
                                  </Typography>
                                </Grid>
                              </Grid>
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          xs={isMobile ? 10 : 3}
                          style={isMobile ? { marginLeft: '30px' } : {}}
                        >
                          <CurrencyTextField
                            className={classes.currencyTextField}
                            style={{ margin: '0px' }}
                            label={''}
                            name={'paymentAmount'}
                            defaultValue={props.oneTimePayConfig.paymentAmount}
                            {...props.paymentInfoFormState.props(
                              'paymentAmount',
                            )}
                            onChange={onPaidAmountChange}
                            disabled={
                              props.oneTimePayConfig.splitPayStatus ||
                              selectedOption === 'RECONNECT_PAY_MINIMUM_DUE' ||
                              selectedOption === 'RECONNECT_PAY_FULL_AMOUNT'
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          xs={isMobile ? 12 : 6}
                          style={!isMobile ? { marginTop: '12px' } : {}}
                        >
                          <Typography variant={'body2'}>
                            {t('OTHER_OPTION_TEXT_PART_3')}
                          </Typography>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Grid>
                </Grid>
              ) : (
                <Grid item container direction="row">
                  <Grid item xs={props?.isInvoicePayment ? 12 :8} className={sliderClasses.columnview}>
                    <Grid className={sliderClasses.infoContainer}>
                      <Grid item container direction="row">
                        <Typography className={sliderClasses.labelText}>
                          {t('AMOUNT_DUE')}
                        </Typography>
                        <Typography className={sliderClasses.labelValue}>
                          $
                          {toCurrencyString(
                            props?.paymentInfo?.amountDue || 0,
                            true,
                          )}
                          {props?.paymentInfo?.amountDue < 0 ? ' CR' : ''}
                        </Typography>
                      </Grid>
                      {props?.paymentInfo?.dueDate !== null ? (
                        <Grid item container direction="row">
                          <Typography className={sliderClasses.labelText}>
                            {t('DUE_DATE')}
                          </Typography>
                          <Typography className={sliderClasses.labelValue}>
                            {props?.paymentInfo?.dueDate
                              ? toDateStringFullMonthName(
                                  parse(
                                    props?.paymentInfo?.dueDate,
                                    'MM/dd/yyyy',
                                    new Date(),
                                  ),
                                  false,
                                )
                              : null}
                          </Typography>
                        </Grid>
                      ) : (
                        <Grid
                          item
                          xs={6}
                          style={{
                            marginLeft: isMobile ? 0 : '3em',
                          }}
                        ></Grid>
                      )}
                      <PaymentMethodSection />
                    </Grid>
                  </Grid>
                  {!props?.isInvoicePayment && (<Grid xs={4} className={sliderClasses.inputContainer}>
                    <Grid item style={{ marginTop: '0.6em' }}>
                      <ClickAwayListener onClickAway={() => toggleOpen(false)}>
                        <Tooltip
                          arrow
                          PopperProps={{
                            disablePortal: true,
                          }}
                          title={
                            <div className={classes.toolTipContent}>
                              <div>{t('PAYMENT_AMOUNT_GREATER_THEN_DUE')}</div>
                              <div>
                                <CloseIcon
                                  className={classes.closeIcon}
                                  onClick={onCloseTooltip}
                                />
                              </div>
                            </div>
                          }
                          disableHoverListener
                          disableTouchListener
                          disableFocusListener
                          classes={classesForToolTip}
                          open={isOpen}
                          placement={isMobile ? 'bottom' : 'right'}
                          onClose={() => toggleOpen(false)}
                        >
                          <div>
                            <CurrencyTextField
                              css={{
                                background: 'transparent !important',
                                '& .MuiInputBase-input': {
                                  background: '#FFF',
                                },
                              }}
                              className={classes.currencyTextField}
                              name={'paymentAmount'}
                              defaultValue={
                                props.oneTimePayConfig.paymentAmount
                              }
                              {...props.paymentInfoFormState.props(
                                'paymentAmount',
                              )}
                              onChange={onPaidAmountChange}
                              disabled={
                                props.oneTimePayConfig.splitPayStatus ||
                                props.disableAmountField
                              }
                            />
                          </div>
                        </Tooltip>
                      </ClickAwayListener>
                    </Grid>
                    {props.enableDonation && props?.paymentInfo?.amountDue > 0 && (
                      <Grid item>
                        <CurrencyTextField
                          label={t('CHARITY_AMOUNT')}
                          className={classes.currencyTextField}
                          name={'charityAmount'}
                          disabled={true}
                          defaultValue={props.oneTimePayConfig?.charityAmount}
                          {...props.paymentInfoFormState.props('charityAmount')}
                          style={
                            props?.oneTimePayConfig?.charitySelection &&
                            props?.oneTimePayConfig?.charitySelection !== 'none'
                              ? { display: 'flex' }
                              : { display: 'none' }
                          }
                        />
                      </Grid>
                    )}
                    <Grid item>
                      <DatePicker
                        css={{
                          width: '100%',

                          '& .MuiFormControl-root': {
                            background: 'transparent',
                          },
                          '& .MuiInputBase-adornedEnd': {
                            background: '#FFF',
                          },
                        }}
                        minDate={today}
                        maxDate={datePickerEndDate}
                        disabled={props?.disableDateField}
                        name={'paymentDate'}
                        label={t('PAYMENT_DATE')}
                        variant={'outlined'}
                        onChange={onDateChange}
                        value={props?.oneTimePayConfig?.paymentDate}
                        minDateMessage={t('DATE_CANNOT_BE_BEFORE_TODAY_DATE')}
                      />
                    </Grid>
                  </Grid>)}
                </Grid>
              )}
            </Grid>
            {props?.paymentInfo?.amountDue > 0 && !props?.isInvoicePayment && (
              <React.Fragment>
                <Grid className={sliderClasses.columnview}>
                  <div ref={sliderRef} style={{ width: '100%' }}>
                    <Slider
                      css={{
                        '& .MuiSlider-track': {
                          background: `linear-gradient(
                        to right,                    
                        #D5F0E4 20%,
                        #43936F 100%
                     )`,
                        },
                        '& .MuiSlider-rail': {
                          background: `linear-gradient(
                      to right,                     
                      ${colors.lightGray1} 20%,
                      ${colors.lightGray1} 100%
                    )`,
                        },
                      }}
                      className={sliderClasses.slider}
                      value={sliderValue}
                      color="primary"
                      onChange={handleSliderChange}
                      onChangeCommitted={handleChangeCommited}
                      disabled={
                        props.oneTimePayConfig.splitPayStatus ||
                        props.disableAmountField ||
                        selectedOption === 'RECONNECT_PAY_MINIMUM_DUE' ||
                        selectedOption === 'RECONNECT_PAY_FULL_AMOUNT'
                      }
                      min={
                        props.minimumAmount &&
                        parseFloat(props.minimumAmount || '0') <
                          props?.paymentInfo?.amountDue
                          ? parseFloat(props.minimumAmount || '0')
                          : 0
                      }
                      max={
                        props.fullAmount &&
                        parseFloat(props.fullAmount || '0') >
                          props?.paymentInfo?.amountDue
                          ? parseFloat(props.fullAmount || '0')
                          : props?.paymentInfo?.amountDue
                      }
                      ThumbComponent={Thumb}
                    />
                  </div>
                </Grid>

                {props.cardFooterContent}

                {props.enableDonation && props?.paymentInfo?.amountDue > 0 && (
                  <React.Fragment>
                    <Grid>
                      <Divider className={sliderClasses.margin1} />
                    </Grid>
                    <Grid
                      container
                      direction="row"
                      className={sliderClasses.columnview}
                    >
                      <CharitySection />
                    </Grid>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </CardContent>
        </Card>
      )}
      {isMobile && (
        <Card className={sliderClasses.mobileCard}>
          <CardContent>
            {props?.isForReconnect ? (
              <Grid
                item
                container
                direction="row"
                className={sliderClasses.reconnectContainer}
              >
                <Grid
                  item
                  container
                  direction={'column'}
                  xs={12}
                  style={{ marginBottom: '1em' }}
                >
                  <Typography
                    className={classes.colorTextPrimary}
                    component={'span'}
                    variant={'h6'}
                    style={{ fontWeight: 'bold' }}
                  >
                    {t('SELECT_PAYMENT_AMOUNT')}
                  </Typography>
                </Grid>
                <Grid item container direction={'column'} xs={12}>
                  <Typography variant="body2">
                    {t('RECONNECT_SUB_TITLE_TEXT')}
                  </Typography>
                </Grid>
                <Grid item container direction={'column'} xs={12}>
                  <RadioGroup
                    value={selectedOption || ''}
                    onChange={e => {
                      handleOptionChange(
                        e.target.value as RECONNECT_PAY_OPTIONS,
                      );
                    }}
                  >
                    <FormControlLabel
                      value={'RECONNECT_PAY_MINIMUM_DUE'}
                      className={classes.formRadioCls}
                      control={
                        <Radio
                          color={'primary'}
                          disabled={props.oneTimePayConfig.splitPayStatus}
                        />
                      }
                      label={
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          spacing={1}
                        >
                          <Grid item>
                            <Typography variant="body2">
                              {t('MIN_OPTION')}
                            </Typography>
                          </Grid>
                          <Grid item style={{ paddingLeft: '0px' }}>
                            <Typography
                              variant="body2"
                              className={classes.radioOptionAmountText}
                            >
                              $
                              {toCurrencyDisplayFormat(
                                Number(props.minimumAmount) || 0,
                                true,
                                'CR',
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                    <FormControlLabel
                      value={'RECONNECT_PAY_FULL_AMOUNT'}
                      className={classes.formRadioCls}
                      control={
                        <Radio
                          color={'primary'}
                          disabled={props.oneTimePayConfig.splitPayStatus}
                        />
                      }
                      label={
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          spacing={1}
                        >
                          <Grid item>
                            <Typography variant="body2">
                              {t('FULL_OPTION')}
                            </Typography>
                          </Grid>
                          <Grid item style={{ paddingLeft: '0px' }}>
                            <Typography
                              variant="body2"
                              className={classes.radioOptionAmountText}
                            >
                              $
                              {toCurrencyDisplayFormat(
                                Number(props.fullAmount) || 0,
                                true,
                                'CR',
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                    <Grid container spacing={1}>
                      <Grid item xs={isMobile ? 12 : 3}>
                        <FormControlLabel
                          className={classes.formRadioCls}
                          style={{ fontSize: '1em', marginRight: '0px' }}
                          value={'RECONNECT_PAY_OTHER_AMOUNT'}
                          control={
                            <Radio
                              color={'primary'}
                              disabled={props.oneTimePayConfig.splitPayStatus}
                            />
                          }
                          label={
                            <Grid
                              container
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Grid item>
                                <Typography variant="body2">
                                  {t('OTHER_OPTION_TEXT_PART_1')}
                                </Typography>
                              </Grid>
                              <Grid item style={{ paddingLeft: '0px' }}>
                                <Typography
                                  variant="body2"
                                  className={classes.radioOptionAmountText}
                                >
                                  {t('OTHER_OPTION_TEXT_PART_2')}
                                </Typography>
                              </Grid>
                            </Grid>
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={isMobile ? 10 : 3}
                        style={isMobile ? { marginLeft: '30px' } : {}}
                      >
                        <CurrencyTextField
                          className={classes.currencyTextField}
                          style={{ margin: '0px' }}
                          label={''}
                          name={'paymentAmount'}
                          defaultValue={props.oneTimePayConfig.paymentAmount}
                          {...props.paymentInfoFormState.props('paymentAmount')}
                          onChange={onPaidAmountChange}
                          disabled={
                            props.oneTimePayConfig.splitPayStatus ||
                            selectedOption === 'RECONNECT_PAY_MINIMUM_DUE' ||
                            selectedOption === 'RECONNECT_PAY_FULL_AMOUNT'
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={isMobile ? 12 : 6}
                        style={!isMobile ? { marginTop: '12px' } : {}}
                      >
                        <Typography variant={'body2'}>
                          {t('OTHER_OPTION_TEXT_PART_3')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </RadioGroup>
                </Grid>
              </Grid>
            ) : (
              <React.Fragment>
                <Grid className={sliderClasses.mobileView}>
                  <Typography variant={'h3'} style={{ textAlign: 'center' }}>
                    {t('CURRENT_AMOUNT_DUE')}
                  </Typography>

                  <Typography
                    className={sliderClasses.labelValue}
                    style={{ textAlign: 'center' }}
                  >
                    ${Math.abs(props?.paymentInfo?.amountDue).toFixed(2)}
                    {props?.paymentInfo?.amountDue < 0 ? ' CR' : ''}
                  </Typography>
                </Grid>

                <Grid style={{ textAlign: 'center' }}>
                  <Typography variant={'body1'}>
                    {richT('DUE_ON_TEXT', {
                      DUE_DATE: props?.paymentInfo?.dueDate
                        ? toDateStringFullMonthName(
                            parse(
                              props?.paymentInfo?.dueDate,
                              'MM/dd/yyyy',
                              new Date(),
                            ),
                          )
                        : undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid className={sliderClasses.mobileView}>
                  <DatePicker
                    style={{ width: '100%' }}
                    minDate={today}
                    maxDate={datePickerEndDate}
                    name={'paymentDate'}
                    label={t('PAYMENT_DATE')}
                    variant={'outlined'}
                    disabled={props?.disableDateField}
                    {...props.paymentInfoFormState.props('paymentDate')}
                  />
                </Grid>
                <Grid
                  className={`${sliderClasses.mobileView} ${sliderClasses.margin1}`}
                >
                  <div style={{ width: '100%' }}>
                    <CurrencyTextField
                      className={classes.currencyTextField}
                      name={'paymentAmount'}
                      defaultValue={props.oneTimePayConfig.paymentAmount}
                      {...props.paymentInfoFormState.props('paymentAmount')}
                      onChange={onPaidAmountChange}
                      disabled={
                        props.oneTimePayConfig.splitPayStatus ||
                        props.disableAmountField
                      }
                    />
                  </div>
                </Grid>
              </React.Fragment>
            )}

            <Grid>
              {' '}
              {props?.paymentInfo?.amountDue > 0 && !props?.isInvoicePayment && (
                <React.Fragment>
                  <div ref={sliderRef} style={{ width: '94%' }}>
                    <Slider
                      css={{
                        '& .MuiSlider-track': {
                          background: `linear-gradient(
                        to right,                      
                        #D5F0E4 20%,
                        #43936F 100%
                     )`,
                        },
                        '& .MuiSlider-rail': {
                          background: `linear-gradient(
                      to right,
                      
                     
                      ${colors.lightGray1} 20%,
                      ${colors.lightGray1} 100%
                    )`,
                        },
                      }}
                      className={sliderClasses.slider}
                      value={sliderValue}
                      color="primary"
                      onChange={handleSliderChange}
                      onChangeCommitted={handleChangeCommited}
                      disabled={
                        props.oneTimePayConfig.splitPayStatus ||
                        props.disableAmountField ||
                        selectedOption === 'RECONNECT_PAY_MINIMUM_DUE' ||
                        selectedOption === 'RECONNECT_PAY_FULL_AMOUNT'
                      }
                      min={
                        props.minimumAmount &&
                        parseFloat(props.minimumAmount || '0') <
                          props?.paymentInfo?.amountDue
                          ? parseFloat(props.minimumAmount || '0')
                          : 0
                      }
                      max={
                        props.fullAmount &&
                        parseFloat(props.fullAmount || '0') >
                          props?.paymentInfo?.amountDue
                          ? parseFloat(props.fullAmount || '0')
                          : props?.paymentInfo?.amountDue
                      }
                      ThumbComponent={Thumb}
                    />
                  </div>
                  {props.cardFooterContent}
                </React.Fragment>
              )}
            </Grid>

            <Grid>
              <PaymentMethodSection />
            </Grid>
          </CardContent>
          {props.enableDonation && props?.paymentInfo?.amountDue > 0 && (
            <>
              <Grid container className={sliderClasses.charityMobileView}>
                <Grid className={sliderClasses.mobileView}>
                  <CharitySection />
                </Grid>

                <Grid
                  className={sliderClasses.mobileView}
                  style={{ width: '100%' }}
                >
                  <CurrencyTextField
                    label={t('CHARITY_AMOUNT')}
                    className={classes.currencyTextField}
                    name={'charityAmount'}
                    disabled={true}
                    defaultValue={props.oneTimePayConfig.charityAmount}
                    {...props.paymentInfoFormState.props('charityAmount')}
                    style={
                      props?.oneTimePayConfig?.charitySelection &&
                      props?.oneTimePayConfig?.charitySelection !== 'none'
                        ? { display: 'flex' }
                        : { display: 'none' }
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Card>
      )}
    </React.Fragment>
  );
};

export default PaymentSlider;
